/**
 * Exportación SUENLACE para A3CON / A3ECO — IMPLEMENTACIÓN OFICIAL.
 *
 * Especificación: "Enlace de entrada en a3asesor Eco y Con para el SII", v9.80
 * (Wolters Kluwer).
 *
 *   - Organización: SECUENCIAL
 *   - Longitud de cada registro: **512 bytes**
 *   - Codificación: Windows-1252
 *   - Separador: CRLF en posiciones 511-512
 *   - Posición 1 SIEMPRE constante "5"   (Tipo de Formato)
 *   - Posiciones 2-6: código de empresa (5 dígitos, "00001"..."99999")
 *   - Posición 509: "E" (Euros)
 *   - Posición 510: "N" (indicador de generado)
 *
 * Cada factura (recibida) genera la siguiente secuencia:
 *
 *   1) Registro tipo "C" — Alta/refresco de la ficha del proveedor.
 *   2) Registro tipo "1" — Cabecera de la factura recibida (importe TOTAL).
 *   3) Registros tipo "9" — Detalle de IVA (uno por cada cuenta de gasto × tramo IVA).
 *      El último lleva indicador "U", los anteriores "M".
 *   4) Registro tipo "V" — Vencimiento del pago.
 *
 * NOTAS IMPORTANTES:
 * - Las cuentas de a3 son de nivel 6 a 12 chars. Esta instalación trabaja con
 *   cuentas de 8 dígitos: las dejamos en su literal de 8 caracteres dentro del
 *   campo de 12 posiciones (rellenado a la derecha con espacios), que a3 acepta.
 * - El nº de factura/documento (pos 59-68 en regs 1, 2, 9, V, B) tiene 10 chars.
 *   Tomamos los 10 últimos dígitos del nº de serie generado (S24123 → "0000024123").
 * - El NIF (pos 176-189 en regs 1/2 y 78-91 en reg C) ocupa 14 chars; va sin prefijo
 *   "ES" para España. Con prefijo solo para no residentes UE.
 */

import { parseNumericLike } from "@/lib/numberFormatting";

import type { SupplierOperationType, SupplierRetentionType } from "@/lib/supplierMaster";

/** Deducibilidad del IVA soportado de una factura recibida. */
export type VatDeductibility = "full" | "none" | "half";

export interface A3ExportRow {
  /** Fecha del asiento contable (ISO YYYY-MM-DD). Si el usuario informó "Fecha contable" en el Libro de IVA, llega aquí. */
  date: string;                     // ISO YYYY-MM-DD
  /** Fecha original de la factura (ISO YYYY-MM-DD). Si no se informa, se usa `date`. Se vuelca en pos 245-252 y en el vencimiento. */
  invoiceDate?: string;
  supplier: string;
  supplierTaxId: string;
  invoiceNumber: string;
  taxableBase: number;
  vatRate: number;
  vatAmount: number;
  irpfRate: number | null;
  irpfAmount: number | null;
  totalAmount: number | null;
  supplierAccountCode: string;
  expenseAccountCode: string;
  supplierDescription: string;
  ivaSeriesNumber: string;
  /** Identificador del asiento contable agrupador (mismo nº para todas las líneas de la misma factura). */
  groupKey: string;
  /** Tipo de operación IVA — controla subtipo SUENLACE (pos 100-101) y cuentas 477/472 (pos 192/228). */
  operationType?: SupplierOperationType;
  /** Tipo de retención del proveedor — controla el código de impreso (pos 173-174). */
  retentionType?: SupplierRetentionType;
  /**
   * Deducibilidad del IVA soportado de la factura (marcada por el usuario):
   *  - "full" (por defecto) → subtipo 01 (interior deducible)
   *  - "none" → subtipo 07 (interior NO deducible)
   *  - "half" → cada tramo se parte en dos registros "9": 50 % subtipo 01 + 50 % subtipo 07
   * Solo aplica a operaciones interiores.
   */
  vatDeductibility?: VatDeductibility;
  /** Líneas adicionales de leasing (carga financiera, seguro) que generan asiento extra de gasto */
  leasingExtraLines?: { amount: number; account: string; label: string }[];
  /** Cuando es leasing, este importe sustituye a la base imponible para la línea de gasto */
  leasingCostRecovery?: number | null;
  leasingCostRecoveryAccount?: string;
  /** Código postal del proveedor (5 dígitos). Vacío si no se conoce. */
  postalCode?: string;
  /**
   * Si está informado (solo en la 1ª línea de la factura, idx===0), el SUENLACE
   * añade un 2º asiento "de pago": Debe cuenta proveedor / Haber `account` por
   * el total de la factura. Se usa para CONTADO y TARJETA cuando el usuario lo
   * marca en la pestaña de revisión.
   */
  paymentEntry?: {
    account: string;
    date?: string;
    kind?: "cash" | "card" | null;
  } | null;
  /** Anotación libre del usuario. Se concatena al concepto del asiento (solo idx===0). */
  notes?: string;
  /**
   * Marca de abono/rectificativa decidida en la revisión (puede venir del
   * detector de texto aunque los importes quedaran en positivo). Si es true,
   * el asiento de pago se genera invertido (DEVOLUCION FRA/) igual que si el
   * total fuera negativo.
   */
  isCreditNote?: boolean;
}

/**
 * Mapeo operationType → subtipo IVA (pos 100-101) + cuentas 477/472 (pos 192/228).
 * Subtipos según spec SUENLACE 9.80; con 03/08 A3 genera automáticamente el
 * doble asiento (auto-repercusión) y alimenta el modelo 349.
 */
const OP_TYPE_CONFIG: Record<SupplierOperationType, { subtipo: string; repercutidoRoot: string; soportadoRoot: string }> = {
  interior:    { subtipo: "01", repercutidoRoot: "",    soportadoRoot: ""    },
  aib:         { subtipo: "03", repercutidoRoot: "477", soportadoRoot: "472" },
  ais:         { subtipo: "08", repercutidoRoot: "477", soportadoRoot: "472" },
  isp:         { subtipo: "04", repercutidoRoot: "477", soportadoRoot: "472" },
  importacion: { subtipo: "06", repercutidoRoot: "",    soportadoRoot: ""    },
};

/** Subtipo SUENLACE de operaciones interiores con IVA NO deducible. */
const SUBTIPO_INTERIOR_NO_DEDUCIBLE = "07";
/** Subtipo SUENLACE para tramos al 12 % (régimen especial agricultura/ganadería). */
const SUBTIPO_REAGP_12 = "02";

/**
 * Código de impreso de retención (pos 173-174 del registro 9). Cada código de
 * dos dígitos determina en qué modelo, clave, naturaleza y tipo de percepción
 * clasifica A3 la retención practicada (especificación SUENLACE v9.80).
 *   05   → modelo 190 (profesionales/actividades), clave G subclave 01,
 *          percepción dineraria.
 *   03   → modelo 115 / alquiler inmobiliario: impreso 180, clave A
 *          dinerarias.
 *   28   → modelo 193, clave C04, Rtos. Arrendamientos B.I dinerarias
 *          (alquiler mobiliario / modelo 123 en la terminología de la app).
 */

/**
 * Impreso para el modelo 190 G01 dinerarias (profesionales 15 % y 7 %).
 */
const IMPRESO_190_G01 = "05";

/**
 * Impreso para el modelo 190 H04 dinerarias (retenciones del 1 %, p. ej.
 * actividades en módulos / agrario-módulos).
 */
const IMPRESO_190_H04 = "09";

/**
 * Impreso para el modelo 190 G03 dinerarias (nuevos profesionales 7 %).
 */
const IMPRESO_190_G03 = "14";

const RETENTION_IMPRESO_CODES: Record<SupplierRetentionType, string> = {
  none: "03",
  mod115: "03",
  mod193: "28",
  mod111: IMPRESO_190_G01,
};

/** Código de impreso cuando la línea NO lleva retención (347 compras-ventas). */
const IMPRESO_SIN_RETENCION = "01";

/**
 * Impreso 349 para adquisiciones intracomunitarias de bienes (clave B).
 */
const IMPRESO_349_BIENES = "02";

/**
 * Impreso 349 para adquisiciones intracomunitarias de servicios (clave S).
 */
const IMPRESO_349_SERVICIOS = "11";

/** Impreso (pos 173-174) para líneas SIN retención según el tipo de operación. */
const impresoSinRetencion = (operationType?: SupplierOperationType | null): string => {
  if (operationType === "aib") return IMPRESO_349_BIENES;
  if (operationType === "ais") return IMPRESO_349_SERVICIOS;
  return IMPRESO_SIN_RETENCION;
};

/**
 * Tipos de IRPF que corresponden siempre al modelo 190 (profesionales 15 %,
 * nuevos profesionales 7 % y actividades en módulos 1 %), nunca al 180.
 */
const MOD_190_IRPF_RATES = [15, 7, 1];

const isMod190Rate = (rate?: number | null): boolean =>
  MOD_190_IRPF_RATES.some((r) => Math.abs((rate ?? 0) - r) < 0.01);

/** Elige el código de impreso del modelo 190 según el % de retención. */
const mod190ImpresoCode = (rate?: number | null): string => {
  const r = rate ?? 0;
  if (Math.abs(r - 1) < 0.01) return IMPRESO_190_H04;
  if (Math.abs(r - 7) < 0.01) return IMPRESO_190_G03;
  return IMPRESO_190_G01;
};

export const retentionImpresoCode = (
  retentionType?: SupplierRetentionType | null,
  irpfRate?: number | null,
): string => {
  // Los tipos propios del 190 mandan sobre el valor por defecto, salvo que el
  // proveedor esté marcado explícitamente como arrendamientos (115 → 180) o
  // alquiler mobiliario (193 → clave C04).
  if (retentionType !== "mod115" && retentionType !== "mod193" && isMod190Rate(irpfRate))
    return mod190ImpresoCode(irpfRate);
  return RETENTION_IMPRESO_CODES[retentionType ?? "none"] ?? "03";
};


/**
 * Subtipo IVA (pos 100-101) a partir del tipo de operación, la deducibilidad y el % de IVA.
 * La no deducibilidad solo se aplica a operaciones interiores: en intracom./ISP/
 * importación manda siempre el subtipo de la operación.
 * Los tramos al 12 % (compensación REAGP) van siempre al subtipo 02.
 */
export const isReagp12Rate = (rate?: number | null): boolean =>
  Math.abs(Math.abs(Number(rate) || 0) - 12) < 0.001;

/**
 * Tipo de IVA efectivo de un tramo. Si la extracción no informó el %, lo
 * deducimos de cuota/base (redondeado a 2 decimales). Sin esto, un tramo de
 * compensación agraria (12 %) salía al SUENLACE con % 00.00 y subtipo 01,
 * y A3 rechazaba el asiento.
 */
export const effectiveVatRate = (
  base?: number | null,
  cuota?: number | null,
  rate?: number | null,
): number => {
  const r = Math.abs(Number(rate) || 0);
  if (r > 0.001) return r;
  const b = Math.abs(Number(base) || 0);
  const c = Math.abs(Number(cuota) || 0);
  if (b < 0.005 || c < 0.005) return 0;
  return Math.round((c / b) * 10000) / 100;
};

const resolveSubtipo = (
  operationType?: SupplierOperationType | null,
  deductibility: VatDeductibility = "full",
  vatRate?: number,
): string => {
  // Si no viene informado (o llega un valor desconocido) asumimos interior.
  const opType: SupplierOperationType =
    operationType && OP_TYPE_CONFIG[operationType] ? operationType : "interior";
  const cfg = OP_TYPE_CONFIG[opType];
  if (opType === "interior" && isReagp12Rate(vatRate)) return SUBTIPO_REAGP_12;
  if (opType === "interior" && deductibility === "none") return SUBTIPO_INTERIOR_NO_DEDUCIBLE;
  return cfg.subtipo;
};





/**
 * Construye una cuenta 472/477 con la MISMA longitud que las cuentas de la
 * empresa (cada cliente puede usar 8, 10, 11 o 12 dígitos). Se rellena con
 * ceros a la derecha, p.ej. "472" + 5 ceros = "47200000". Si no se puede
 * inferir, se asume 8 dígitos (lo más habitual en A3).
 */
const buildVatAccount = (root: string, referenceAccounts: (string | null | undefined)[]): string => {
  if (!root) return "";
  const lengths = referenceAccounts
    .map((a) => (a ?? "").replace(/[^0-9]/g, "").length)
    .filter((n) => n >= 6 && n <= 12);
  const targetLen = lengths.length > 0 ? Math.max(...lengths) : 8;
  return root.padEnd(targetLen, "0");
};

export interface A3ExportOptions {
  /** Código de empresa A3 (5 dígitos, se rellena con ceros a la izquierda). */
  companyCode: string;
  /** Número inicial del asiento (no se usa explícitamente — A3 lo recalcula al importar). */
  startEntryNumber: number;
  /** Ajuste por asesoría: usar la fecha de la factura como fecha de contabilización. */
  useInvoiceDateAsEntryDate?: boolean;
  /** Ajuste por asesoría: no traspasar el nº de registro de IVA (se usa el nº de factura). */
  omitVatLedgerNumber?: boolean;
}

// ── Constantes oficiales A3 ───────────────────────────────────────────────
const LINE_LENGTH = 512;
const FORMAT_TAG = "5";          // Posición 1 — constante
const CURRENCY_TAG = "E";        // Posición 509 — Euros
const GENERATED_TAG = "N";       // Posición 510 — Indicador de generado

// ── Helpers de formateo ───────────────────────────────────────────────────
const padLeft = (value: string, width: number, char = "0"): string =>
  value.length >= width ? value.slice(-width) : char.repeat(width - value.length) + value;

const padRight = (value: string, width: number, char = " "): string =>
  value.length >= width ? value.slice(0, width) : value + char.repeat(width - value.length);

/**
 * Cuenta a3 (campo de 12 posiciones, niveles 6..12).
 * Se respeta la longitud real introducida por el usuario (cada empresa puede
 * tener su propio nivel: 8, 10, 11 o 12 dígitos). El campo se alinea a la
 * izquierda y se rellena a la derecha con espacios hasta 12 posiciones, que
 * es lo que A3 espera. Truncar a 8 provocaba que A3 inventara cuentas al
 * rellenar con ceros las posiciones libres (p.ej. 41000009886 → 41000009000).
 */
const padAccount12 = (s: string | null | undefined): string => {
  const digits = (s ?? "").replace(/[^0-9]/g, "");
  if (!digits) return padRight("", 12);
  // No truncar a 8: respetar los dígitos originales hasta 12 posiciones.
  // Si excede 12 (caso no previsto en A3), recortamos por la derecha.
  const acc = digits.length > 12 ? digits.slice(0, 12) : digits;
  return padRight(acc, 12);
};

/** Formato A3 de importes: signo + 10 enteros + "." + 2 decimales = 14 chars. */
const formatA3Amount = (amount: number): string => {
  const sign = amount < 0 ? "-" : "+";
  const abs = Math.abs(amount);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  // Si el redondeo desborda (.999... → 1.00), arrastrar al entero
  if (decPart >= 100) {
    return sign + padLeft(String(intPart + 1), 10) + "." + "00";
  }
  return sign + padLeft(String(intPart), 10) + "." + padLeft(String(decPart), 2);
};

/** Formato A3 de porcentaje: 2 enteros + "." + 2 decimales = 5 chars. */
const formatA3Rate = (rate: number): string => {
  const abs = Math.abs(rate);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  if (decPart >= 100) {
    return padLeft(String(intPart + 1), 2) + "." + "00";
  }
  return padLeft(String(intPart), 2) + "." + padLeft(String(decPart), 2);
};

/** Fecha A3: aaaammdd (8 chars). */
const formatA3Date = (iso: string): string => {
  if (!iso) return "        ";
  const isoMatch = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
  const slashMatch = iso.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) return `${slashMatch[3]}${slashMatch[2]}${slashMatch[1]}`;
  return "        ";
};

const cleanText = (s: string | null | undefined): string =>
  (s ?? "").replace(/[\r\n\t]/g, " ").replace(/\s+/g, " ").trim();

/**
 * Códigos de país numéricos A3 (pos 175-177 en reg "C") para los proveedores
 * más habituales. Cuando el país no está en la tabla caemos a "011" (España)
 * — el campo alfa de 2 letras del reg de cabecera (pos 374-375) es el que A3
 * usa realmente para identificar al no residente, así que esto es seguro.
 */
const A3_COUNTRY_NUMERIC: Record<string, string> = {
  ES: "011", FR: "109", DE: "067", IT: "103", PT: "165", IE: "153",
  NL: "137", BE: "017", AT: "002", LU: "129", DK: "057", SE: "203",
  FI: "077", PL: "163", CZ: "055", SK: "189", HU: "099", RO: "175",
  GR: "087", EL: "087", BG: "043", HR: "053", SI: "187", EE: "071",
  LV: "117", LT: "121", MT: "133", CY: "049", GB: "069", XI: "069",
  CH: "047", NO: "143", US: "207", MX: "131",
};

const detectForeignPrefix = (
  raw: string | null | undefined,
): { country: string; rest: string } | null => {
  const id = (raw ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (id.length < 4) return null;
  const prefix = id.slice(0, 2);
  if (prefix === "ES") return null;
  if (!/^[A-Z]{2}$/.test(prefix)) return null;
  if (!A3_COUNTRY_NUMERIC[prefix]) return null;
  return { country: prefix, rest: id.slice(2) };
};

/**
 * NIF para a3 — sin prefijo "ES" para España (a3 lo asume).
 * Para no residentes UE / terceros conserva el prefijo de país (IE, FR…)
 * dentro del propio campo NIF (14 chars).
 */
const cleanTaxId = (taxId: string | null | undefined): string => {
  const raw = cleanText(taxId).toUpperCase().replace(/[\s.\-/]/g, "");
  if (raw.startsWith("ES") && raw.length === 11) return raw.slice(2); // ES + 9 chars
  return raw;
};

/**
 * Nº de factura/documento para a3 (10 chars).
 *
 * Se respeta el valor "Serie/Nº" tal cual aparece en el Libro de IVA: SIN
 * rellenar con ceros a la izquierda. El campo se alinea a la izquierda y
 * se rellena con espacios a la derecha hasta los 10 caracteres. Si supera
 * los 10 caracteres, se trunca.
 *
 * Si no hay serie informada, caemos al nº de factura original respetando
 * el mismo criterio (sin ceros a la izquierda).
 */
const buildDocumentNumber = (invoiceNumber: string, ivaSeriesNumber?: string): string => {
  const series = cleanText(ivaSeriesNumber ?? "");
  if (series) return padRight(series.slice(0, 10), 10);
  const inv = cleanText(invoiceNumber).toUpperCase();
  if (inv) return padRight(inv.slice(0, 10), 10);
  return padRight("", 10);
};

/**
 * Concepto del apunte (descripción, máx 30 chars).
 *
 * Se respeta EXCLUSIVAMENTE el nº de factura del proveedor tal cual aparece
 * en la celda. NO se añade ningún year tag ni prefijo numérico.
 */
const invoiceConcept = (invoiceNumber: string): string => {
  const cleanInv = cleanText(invoiceNumber).toUpperCase();
  return `Su Fra. ${cleanInv}`.slice(0, 30);
};


// ── Codificación Windows-1252 ─────────────────────────────────────────────
const WIN1252_FALLBACK = 0x3F; // '?'

const encodeWin1252 = (text: string): Uint8Array => {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes.push(code);
    else if (code >= 0xA0 && code <= 0xFF) bytes.push(code);
    else {
      switch (code) {
        case 0x20AC: bytes.push(0x80); break;
        case 0x201A: bytes.push(0x82); break;
        case 0x0192: bytes.push(0x83); break;
        case 0x201E: bytes.push(0x84); break;
        case 0x2026: bytes.push(0x85); break;
        case 0x2020: bytes.push(0x86); break;
        case 0x2021: bytes.push(0x87); break;
        case 0x02C6: bytes.push(0x88); break;
        case 0x2030: bytes.push(0x89); break;
        case 0x0160: bytes.push(0x8A); break;
        case 0x2018: bytes.push(0x91); break;
        case 0x2019: bytes.push(0x92); break;
        case 0x201C: bytes.push(0x93); break;
        case 0x201D: bytes.push(0x94); break;
        case 0x2022: bytes.push(0x95); break;
        case 0x2013: bytes.push(0x96); break;
        case 0x2014: bytes.push(0x97); break;
        default:     bytes.push(WIN1252_FALLBACK);
      }
    }
  }
  return new Uint8Array(bytes);
};

/**
 * Construye una línea de exactamente 510 caracteres (las posiciones 511-512
 * son el CRLF que añadiremos al concatenar). Posiciones 1-indexed.
 *
 * Escribe automáticamente:
 *   - Pos 1   → "5" (Tipo de Formato)
 *   - Pos 509 → "E" (Moneda)
 *   - Pos 510 → "N" (Indicador de generado)
 */
const PAYLOAD_LENGTH = 510; // sin contar el CRLF

const buildLine = (fields: { pos: number; value: string }[]): string => {
  const buf = new Array(PAYLOAD_LENGTH).fill(" ");
  // Constantes obligatorias
  buf[0] = FORMAT_TAG;          // Pos 1
  buf[508] = CURRENCY_TAG;      // Pos 509
  buf[509] = GENERATED_TAG;     // Pos 510
  // Campos posicionales del registro
  for (const f of fields) {
    const idx = f.pos - 1;
    for (let i = 0; i < f.value.length && idx + i < PAYLOAD_LENGTH; i++) {
      buf[idx + i] = f.value[i];
    }
  }
  return buf.join("");
};

// ── Agrupación de filas por factura ───────────────────────────────────────
interface InvoiceGroup {
  groupKey: string;
  ivaSeriesNumber: string;
  date: string;
  invoiceDate: string;
  invoiceNumber: string;
  supplier: string;
  supplierDescription: string;
  supplierTaxId: string;
  supplierAccount: string;
  totalAmount: number;
  irpfRate: number;
  irpfAmount: number;
  vatLines: { taxableBase: number; vatRate: number; vatAmount: number; expenseAccount: string }[];
  leasingExtraLines: { amount: number; account: string }[];
  leasingCostRecovery: number | null;
  leasingCostRecoveryAccount: string;
  postalCode: string;
  paymentEntry: { account: string; date: string; kind: "cash" | "card" | null } | null;
  operationType: SupplierOperationType;
  retentionType: SupplierRetentionType;
  vatDeductibility: VatDeductibility;
  notes: string;
  isCreditNote: boolean;
}

const groupRows = (rows: A3ExportRow[]): InvoiceGroup[] => {
  const map = new Map<string, InvoiceGroup>();
  for (const r of rows) {
    let g = map.get(r.groupKey);
    if (!g) {
      g = {
        groupKey: r.groupKey,
        ivaSeriesNumber: cleanText(r.ivaSeriesNumber),
        date: r.date,
        invoiceDate: r.invoiceDate || r.date,
        invoiceNumber: r.invoiceNumber,
        supplier: r.supplier,
        supplierDescription: cleanText(r.supplierDescription) || r.supplier,
        supplierTaxId: r.supplierTaxId,
        supplierAccount: r.supplierAccountCode,
        totalAmount: parseNumericLike(r.totalAmount ?? 0),
        irpfRate: parseNumericLike(r.irpfRate ?? 0),
        irpfAmount: parseNumericLike(r.irpfAmount ?? 0),
        vatLines: [],
        leasingExtraLines: r.leasingExtraLines?.map((l) => ({ amount: l.amount, account: l.account })) ?? [],
        leasingCostRecovery: r.leasingCostRecovery ?? null,
        leasingCostRecoveryAccount: r.leasingCostRecoveryAccount ?? "",
        postalCode: (r.postalCode ?? "").replace(/\D/g, "").slice(0, 5),
        paymentEntry: r.paymentEntry && r.paymentEntry.account
          ? { account: r.paymentEntry.account, date: r.paymentEntry.date || r.date, kind: r.paymentEntry.kind ?? null }
          : null,
        operationType: r.operationType ?? "interior",
        retentionType: r.retentionType ?? "none",
        vatDeductibility: r.vatDeductibility ?? "full",
        notes: cleanText(r.notes ?? ""),
        isCreditNote: !!r.isCreditNote,
      };
      map.set(r.groupKey, g);
    } else {
      if (r.operationType && g.operationType === "interior") {
        g.operationType = r.operationType;
      }
      if (r.retentionType && r.retentionType !== "none" && g.retentionType === "none") {
        g.retentionType = r.retentionType;
      }
      if (r.vatDeductibility && r.vatDeductibility !== "full" && g.vatDeductibility === "full") {
        g.vatDeductibility = r.vatDeductibility;
      }
    }
    g.vatLines.push({
      taxableBase: parseNumericLike(r.taxableBase),
      vatRate: parseNumericLike(r.vatRate),
      vatAmount: parseNumericLike(r.vatAmount),
      expenseAccount: r.expenseAccountCode,
    });
  }
  return Array.from(map.values());
};

// ── Constructores de cada tipo de registro ────────────────────────────────

/**
 * Registro "C" — Alta/modificación de cuenta proveedor.
 *
 *   1       "5" (Tipo de formato)
 *   2-6     Código empresa (5)
 *   7-14    Fecha de alta (aaaammdd)
 *   15      "C"
 *   16-27   Cuenta (12)
 *   28-57   Descripción cuenta (30)
 *   58      "N" — no actualizar saldo inicial
 *   59-72   Saldo inicial (14)         +0000000000.00
 *   73      " " — Ampliación (no es CCC)
 *   74-77   Reserva (4)
 *   78-91   NIF (14)
 *   92-93   Siglas vía pública (2)
 *   94-123  Vía pública (30)
 *   124-128 Número (5)
 *   135-154 Municipio (20)
 *   155-159 Código postal (5)
 *   160-174 Provincia (15)
 *   175-177 País (3) — "011" España (default si vacío)
 *   509     "E"
 *   510     "N"
 */
const buildSupplierAccountRecord = (
  empresa: string,
  date: string,
  supplierAccount: string,
  supplierName: string,
  taxId: string,
  postalCode: string,
): string => {
  const foreign = detectForeignPrefix(taxId);
  const countryNumeric = foreign ? A3_COUNTRY_NUMERIC[foreign.country] : "011";
  const fields: { pos: number; value: string }[] = [
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "C" },
    { pos: 16,  value: padAccount12(supplierAccount) },
    { pos: 28,  value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58,  value: "N" },
    { pos: 59,  value: formatA3Amount(0) },
    { pos: 73,  value: " " },
    { pos: 78,  value: padRight(cleanTaxId(taxId), 14) },
    { pos: 175, value: countryNumeric },
  ];
  // Código postal en posiciones 155-159 (5 chars). Solo si tenemos uno válido.
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) {
    fields.push({ pos: 155, value: padLeft(cp.slice(0, 5), 5) });
  }
  return buildLine(fields);
};

/**
 * Registro "1" o "2" — Cabecera factura recibida (con IVA).
 *
 *   1       "5"
 *   2-6     Empresa (5)
 *   7-14    Fecha del apunte (aaaammdd)
 *   15      "1" facturas | "2" rectificativas
 *   16-27   Cuenta proveedor (12)
 *   28-57   Descripción cuenta (30)
 *   58      Tipo de factura: "1"=Ventas, "2"=Compras, "3"=Bienes Inversión
 *   59-68   Nº factura/documento (10)
 *   69      "I" (inicio apunte)
 *   70-99   Descripción del apunte (30)
 *   100-113 Importe TOTAL (14)
 *   176-189 NIF (14)
 *   190-229 Nombre cliente/proveedor (40)
 *   230-234 Código postal (5)
 *   245-252 Fecha de factura (aaaammdd)
 *   253-312 Nº factura ampliado SII (60)
 *   374-375 País (2 alfa) — "ES"
 *   376-377 Tipo documento — "01" NIF español (en blanco para residentes simples)
 *   509/510 → automáticos
 */
const buildInvoiceHeaderRecord = (
  empresa: string,
  date: string,
  isRectificativa: boolean,
  supplierAccount: string,
  supplierName: string,
  documentNumber: string,
  totalAmount: number,
  taxId: string,
  concept: string,
  invoiceNumber: string,
  postalCode: string,
  invoiceDate: string,
  paymentEntry?: { account: string; date: string } | null,
): string => {
  const cleanedTaxId = cleanTaxId(taxId);
  const foreign = detectForeignPrefix(taxId);
  const fields: { pos: number; value: string }[] = [
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: isRectificativa ? "2" : "1" },
    { pos: 16,  value: padAccount12(supplierAccount) },
    { pos: 28,  value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58,  value: "2" },
    { pos: 59,  value: documentNumber },
    { pos: 69,  value: "I" },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 176, value: padRight(cleanedTaxId, 14) },
    { pos: 190, value: padRight(cleanText(supplierName).toUpperCase(), 40) },
    { pos: 245, value: formatA3Date(date) },
    { pos: 253, value: padRight(cleanText(invoiceNumber).toUpperCase(), 60) },
    // pos 374-375 País (2 alfa). Para residentes lo dejamos vacío (a3 asume ES);
    // para no residentes informamos el ISO-2 (IE, FR, DE…). pos 376-377 (Tipo
    // documento) queda en blanco para extranjeros — A3 lo interpreta como
    // "pasaporte / NIF-IVA extranjero" y deja de validar el dígito de control.
    { pos: 374, value: foreign ? foreign.country : "  " },
  ];
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) {
    fields.push({ pos: 230, value: padLeft(cp.slice(0, 5), 5) });
  }
  // Nota: la "Cta. Tesorería" de la cancelación NO va en este registro de
  // cabecera (pos 313-373 es RESERVA en la spec SUENLACE 9.80). El asiento de
  // pago se genera mediante los registros V + V-A (ampliación) emitidos a
  // continuación de la factura.
  void paymentEntry;
  return buildLine(fields);
};

/**
 * Registro "9" — Detalle de IVA (una línea por tramo y/o cuenta de gasto).
 *
 *   1       "5"
 *   2-6     Empresa (5)
 *   7-14    Fecha del apunte (aaaammdd)
 *   15      "9"
 *   16-27   Cuenta de gasto (12)
 *   28-57   Descripción de la cuenta (30)
 *   58      "C"=Cargo en factura | "A"=Abono en factura
 *   59-68   Nº factura (10)
 *   69      "M" (medio) | "U" (último)
 *   70-99   Descripción del apunte (30)
 *   100-101 Subtipo IVA — "01" Operaciones interiores IVA deducible
 *   102-115 Base imponible (14)
 *   116-120 % IVA (5)
 *   121-134 Cuota IVA (14)
 *   135-139 % Recargo equivalencia (5)
 *   140-153 Cuota Recargo (14)
 *   154-158 % Retención (5)
 *   159-172 Cuota Retención (14)
 *   173-174 Impreso — "01" 347 A-B (compras-ventas)
 *   175     "S" Operación con IVA / "N" sin IVA
 *   192-203 Cuenta IVA Soportado (12) — la deja a3 por defecto si vacía
 */
const buildVatDetailRecord = (
  empresa: string,
  date: string,
  expenseAccount: string,
  expenseDesc: string,
  documentNumber: string,
  concept: string,
  marker: "M" | "U",
  base: number,
  rate: number,
  cuota: number,
  irpfRate: number,
  irpfAmount: number,
  operationType: SupplierOperationType = "interior",
  referenceAccounts: (string | null | undefined)[] = [],
  /** Subtipo IVA explícito (pos 100-101). Si se omite, se deriva del tipo de operación. */
  subtipoOverride?: string,
  /** Raíz de la cuenta de IVA soportado a forzar en pos 192 (p.ej. "472" en REAGP 12 %). */
  soportadoRootOverride?: string,
  /** Cuenta de retención completa a forzar en pos 216 (p.ej. "47510001"). */
  retencionAccountOverride?: string,
  /** Tipo de retención del proveedor (clasifica el impreso en pos 173-174). */
  retentionType?: SupplierRetentionType,
): string => {
  // Subtipo IVA (pos 100-101) según operationType — ver OP_TYPE_CONFIG.
  // Pos 175 siempre "S" para que A3 no marque "configuración incompleta".
  // En interiores, pos 192 = IVA soportado. Con autorrepercusión, A3 interpreta
  // pos 192 como Haber (477) y pos 228 como Debe (472), según el traspaso real
  // contrastado el 20/10/2026. Pos 216 sigue reservada a retención (4751).
  // Se construyen con la misma longitud (nivel) que las demás cuentas de la
  // empresa para que A3 las reconozca.
  const cfg = OP_TYPE_CONFIG[operationType] ?? OP_TYPE_CONFIG.interior;
  const refAccs = [expenseAccount, ...referenceAccounts];
  const repercutido = buildVatAccount(cfg.repercutidoRoot, refAccs);
  const soportado   = buildVatAccount(soportadoRootOverride || cfg.soportadoRoot, refAccs);
  const retencion   = irpfAmount > 0 || irpfRate > 0
    ? (retencionAccountOverride || buildVatAccount("4751", refAccs))
    : "";


  const fields: { pos: number; value: string }[] = [
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "9" },
    { pos: 16,  value: padAccount12(expenseAccount) },
    { pos: 28,  value: padRight(cleanText(expenseDesc).toUpperCase(), 30) },
    { pos: 58,  value: "C" },
    { pos: 59,  value: documentNumber },
    { pos: 69,  value: marker },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: subtipoOverride || cfg.subtipo },
    { pos: 102, value: formatA3Amount(base) },
    { pos: 116, value: formatA3Rate(rate) },
    { pos: 121, value: formatA3Amount(cuota) },
    { pos: 135, value: formatA3Rate(0) },
    { pos: 140, value: formatA3Amount(0) },
    { pos: 154, value: formatA3Rate(irpfRate) },
    { pos: 159, value: formatA3Amount(irpfAmount) },
    // Código de impreso: con retención, el de la retención (180/190/193);
    // sin retención, intracomunitarias de bienes → "02" (349 B) y de
    // servicios → "11" (349 S); el resto → "01" (347 compras-ventas).
    {
      pos: 173,
      value: irpfAmount > 0 || irpfRate > 0
        ? retentionImpresoCode(retentionType, irpfRate)
        : impresoSinRetencion(operationType),
    },
    { pos: 175, value: "S" },
  ];
  const isAutoRepercussion = operationType === "aib" || operationType === "ais" || operationType === "isp";
  const account192 = isAutoRepercussion ? repercutido : soportado;
  const account228 = isAutoRepercussion ? soportado : repercutido;
  if (account192) fields.push({ pos: 192, value: padRight(account192, 12) });
  if (retencion)   fields.push({ pos: 216, value: padRight(retencion,   12) });
  if (account228) fields.push({ pos: 228, value: padRight(account228, 12) });

  return buildLine(fields);
};

/**
 * Registro "V" — Alta de Vencimiento (SUENLACE 9.80, página 26).
 *
 *   1       "5"
 *   2-6     Empresa (5)
 *   7-14    Fecha vencimiento (aaaammdd)
 *   15      "V"
 *   16-27   Cuenta proveedor (12)
 *   28-57   Descripción cuenta (30)
 *   58      "P" (Pago — factura recibida)
 *   59-68   Nº factura (10)
 *   69      " " (constante: vencimiento normal, no ampliación)
 *   70-99   Descripción del vencimiento (30)
 *   100-113 Importe del vencimiento (14)
 *   114-121 Fecha de la factura (aaaammdd)
 *   122-133 Cuenta de tesorería (12)  ← caja/tarjeta, si se conoce
 *   134-135 Forma de pago (01-99)     ← en blanco si no se conoce
 *   136-137 Número de vencimiento     ← en blanco
 */
const buildDueDateRecord = (
  empresa: string,
  dueDate: string,
  supplierAccount: string,
  supplierName: string,
  documentNumber: string,
  invoiceNumber: string,
  totalAmount: number,
  invoiceDate: string,
  paymentEntry?: { account: string; date: string } | null,
): string => {
  const cleanInv = cleanText(invoiceNumber).toUpperCase();
  const concept = `VTO FRA/${cleanInv}`.slice(0, 30);
  const fields: { pos: number; value: string }[] = [
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(dueDate) },
    { pos: 15,  value: "V" },
    { pos: 16,  value: padAccount12(supplierAccount) },
    { pos: 28,  value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58,  value: "P" },
    { pos: 59,  value: documentNumber },
    { pos: 69,  value: " " },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 114, value: formatA3Date(invoiceDate) },
  ];
  if (paymentEntry && paymentEntry.account) {
    fields.push({ pos: 122, value: padAccount12(paymentEntry.account) });
  }
  return buildLine(fields);
};

/**
 * Registro "0" — Apunte sin IVA (SUENLACE 9.80, página 2-3).
 * Se usa para generar un asiento manual de cancelación de pago:
 *   Línea I (Debe)  → cuenta del proveedor
 *   Línea U (Haber) → cuenta de tesorería (caja / tarjeta)
 *
 *   1       "5"
 *   2-6     Empresa (5)
 *   7-14    Fecha del apunte (aaaammdd)
 *   15      "0"
 *   16-27   Cuenta (12)
 *   28-57   Descripción de la cuenta (30)
 *   58      "D" Debe / "H" Haber
 *   59-68   Referencia documento (10)
 *   69      "I" inicio / "M" medio / "U" último
 *   70-99   Descripción del apunte (30)
 *   100-113 Importe (14)
 */
const buildSimpleEntryRecord = (
  empresa: string,
  date: string,
  account: string,
  description: string,
  side: "D" | "H",
  reference: string,
  marker: "I" | "M" | "U",
  concept: string,
  amount: number,
): string =>
  buildLine([
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "0" },
    { pos: 16,  value: padAccount12(account) },
    { pos: 28,  value: padRight(cleanText(description).toUpperCase(), 30) },
    { pos: 58,  value: side },
    { pos: 59,  value: reference },
    { pos: 69,  value: marker },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(Math.abs(amount)) },
  ]);


// ── API pública ───────────────────────────────────────────────────────────
export interface A3BuildResult {
  bytes: Uint8Array;
  fileName: string;
  invoiceCount: number;
  lineCount: number;
  warnings: string[];
}

export const validateA3Rows = (rows: A3ExportRow[]): string[] => {
  const warnings: string[] = [];
  const groups = groupRows(rows);
  for (const g of groups) {
    if (!g.supplierTaxId) warnings.push(`Falta NIF en factura ${g.invoiceNumber || "(s/n)"}`);
    if (!g.supplierAccount) warnings.push(`Falta cuenta de proveedor en factura ${g.invoiceNumber || "(s/n)"} (${g.supplier})`);
    if (g.vatDeductibility !== "full" && g.operationType !== "interior") {
      warnings.push(
        `La factura ${g.invoiceNumber || "(s/n)"} (${g.supplier}) está marcada como IVA no deducible, pero es una operación no interior: se exporta con el subtipo de su tipo de operación.`,
      );
    }
    const isLeasing = !!(g.leasingCostRecovery && g.leasingCostRecovery !== 0);
    for (const v of g.vatLines) {
      if (!isLeasing && !v.expenseAccount) {
        warnings.push(`Falta cuenta de gasto en factura ${g.invoiceNumber || "(s/n)"} (${g.supplier})`);
        break;
      }
    }
  }
  return warnings;
};

/**
 * Cuentas de retención específicas por empresa y % de retención.
 * ENORA SERVICIOS DE ALMACENAJE (empresa 00029): retenciones del 19 % a 47510001.
 */
const RETENTION_ACCOUNT_RULES: { empresa: string; rate: number; account: string }[] = [
  { empresa: "00029", rate: 19, account: "47510001" },
];

const resolveRetentionAccount = (empresa: string, irpfRate: number): string | undefined =>
  RETENTION_ACCOUNT_RULES.find(
    (r) => r.empresa === empresa && Math.abs((irpfRate || 0) - r.rate) < 0.01,
  )?.account;


export const buildA3SuenlaceFile = (
  rows: A3ExportRow[],
  options: A3ExportOptions,
): A3BuildResult => {
  // Empresa: 5 dígitos según especificación oficial (00001..99999)
  const empresa = padLeft((options.companyCode || "").replace(/\D/g, ""), 5);
  const groups = groupRows(rows);
  const lines: string[] = [];
  const warnings = validateA3Rows(rows);

  for (const g of groups) {
    if (!g.supplierAccount) continue;

    const entryDate = options.useInvoiceDateAsEntryDate ? (g.invoiceDate || g.date) : g.date;
    const documentNumber = buildDocumentNumber(
      g.invoiceNumber,
      options.omitVatLedgerNumber ? "" : g.ivaSeriesNumber,
    );
    const concept = invoiceConcept(g.invoiceNumber);
    // Factura de abono / rectificativa: por total negativo o por marca manual
    // (el detector puede marcarla aunque los importes quedaran en positivo).
    const isRectificativa = g.totalAmount < 0 || g.isCreditNote;
    const totalForA3 = Math.abs(g.totalAmount); // a3 quiere positivo en rectificativas que disminuyen

    // 1) Alta de ficha proveedor (tipo C)
    lines.push(buildSupplierAccountRecord(empresa, entryDate, g.supplierAccount, g.supplierDescription, g.supplierTaxId, g.postalCode));

    // 2) Cabecera factura (tipo 1 o 2)
    lines.push(buildInvoiceHeaderRecord(
      empresa,
      entryDate,
      isRectificativa,
      g.supplierAccount,
      g.supplierDescription,
      documentNumber,
      totalForA3,
      g.supplierTaxId,
      concept,
      g.invoiceNumber,
      g.postalCode,
      g.invoiceDate,
      g.paymentEntry,
    ));

    // 3) Detalle IVA (tipo 9). Una línea por (cuenta gasto + tramo IVA).
    const isLeasing = !!(g.leasingCostRecovery && g.leasingCostRecovery !== 0);
    type Line9 = { account: string; base: number; rate: number; cuota: number };
    const expenseLines: Line9[] = [];

    if (isLeasing && g.leasingCostRecoveryAccount) {
      let totalBase = g.leasingCostRecovery!;
      let totalCuota = 0;
      let dominantRate = 0;
      for (const v of g.vatLines) {
        totalCuota += v.vatAmount;
        if (Math.abs(v.taxableBase) > Math.abs(g.leasingCostRecovery! / 2)) {
          dominantRate = effectiveVatRate(v.taxableBase, v.vatAmount, v.vatRate);
        }
      }
      expenseLines.push({ account: g.leasingCostRecoveryAccount, base: Math.abs(totalBase), rate: dominantRate, cuota: Math.abs(totalCuota) });
    } else {
      const acc = new Map<string, Line9>();
      for (const v of g.vatLines) {
        if (!v.expenseAccount) continue;
        const vRate = effectiveVatRate(v.taxableBase, v.vatAmount, v.vatRate);
        const key = `${v.expenseAccount}|${vRate}`;
        const existing = acc.get(key);
        if (existing) {
          existing.base += v.taxableBase;
          existing.cuota += v.vatAmount;
        } else {
          acc.set(key, { account: v.expenseAccount, base: v.taxableBase, rate: vRate, cuota: v.vatAmount });
        }
      }
      for (const v of acc.values()) {
        // En rectificativas (factura completa negativa) A3 espera todas las
        // líneas en positivo con cabecera tipo "2". En una factura normal,
        // en cambio, hay que preservar el signo de cada línea: si una parte
        // de la base es negativa (p. ej. descuento contra 740), debe salir
        // negativa al SUENLACE — si no, el asiento queda descuadrado.
        const sign = isRectificativa ? 1 : Math.sign(v.base) || 1;
        expenseLines.push({ ...v, base: Math.abs(v.base) * sign, cuota: Math.abs(v.cuota) * sign });
      }
    }

    // Líneas extra de leasing (carga financiera, seguro) → cuentas de gasto adicionales sin IVA
    for (const l of g.leasingExtraLines) {
      if (!l.account || !l.amount) continue;
      const sign = isRectificativa ? 1 : Math.sign(l.amount) || 1;
      expenseLines.push({ account: l.account, base: Math.abs(l.amount) * sign, rate: 0, cuota: 0 });
    }


    // marker A3: 'M' = hay siguiente registro 9; 'U' = último (o único) registro 9
    const hasReagp12 = g.vatLines.some((line) =>
      isReagp12Rate(effectiveVatRate(line.taxableBase, line.vatAmount, line.vatRate)));
    const informedIrpfAbs = Math.abs(g.irpfAmount);
    // No recalcular el % si ya viene informado: en facturas pequeñas, la cuota
    // redondeada puede convertir un 15% real en 15,01% (p.ej. 8,21 / 54,70),
    // y A3 no encuentra la retención configurada para ese porcentaje inventado.
    const irpfRate = informedIrpfAbs > 0 && g.irpfRate > 0
      ? Math.round(Math.abs(g.irpfRate) * 100) / 100
      : informedIrpfAbs > 0 && expenseLines.length > 0
      ? Math.round((informedIrpfAbs / expenseLines.reduce((s, l) => s + l.base, 0)) * 10000) / 100
      : 0;
    // En REAGP la retención (habitualmente 2 %) se practica sobre la base más
    // la compensación agraria. Recalculamos al exportar para corregir sesiones
    // antiguas que guardaron la retención únicamente sobre la base imponible.
    const irpfAbs = hasReagp12 && irpfRate > 0
      ? Math.round(g.vatLines.reduce((sum, line) => {
          const r = effectiveVatRate(line.taxableBase, line.vatAmount, line.vatRate);
          if (r <= 0) return sum;
          return sum + Math.abs(line.taxableBase) + (isReagp12Rate(r) ? Math.abs(line.vatAmount) : 0);
        }, 0) * irpfRate) / 100
      : informedIrpfAbs;

    // Cuentas de referencia para inferir el nivel (8/10/11/12 dígitos) que
    // usa la empresa, y construir 472/477 con esa misma longitud.
    const refAccs: (string | null | undefined)[] = [
      g.supplierAccount,
      ...expenseLines.map((l) => l.account),
    ];

    // Para AIB/AIS/ISP el IVA repercutido/soportado lo genera el receptor:
    // la factura llega con base y cuota=0, pero en el SUENLACE debemos enviar
    // el registro "9" con rate=21 % y cuota=base×21 %. Con eso + subtipo
    // (03/08/04) en pos 100-101 + 477 en pos 192 y 472 en pos 228, A3 genera
    // automáticamente el asiento de auto-repercusión (Debe 472 / Haber 477) y
    // alimenta el modelo 349 cuando procede.
    const isAutoRepercussion =
      g.operationType === "aib" || g.operationType === "ais" || g.operationType === "isp";

    // Deducibilidad del IVA (solo aplica a operaciones interiores):
    //  - "none" → todas las líneas con subtipo 07 (interior NO deducible)
    //  - "half" → cada línea se parte en dos: 50 % subtipo 01 + 50 % subtipo 07.
    //    El descuadre por redondeo lo absorbe la segunda mitad.
    const opType: SupplierOperationType =
      g.operationType && OP_TYPE_CONFIG[g.operationType] ? g.operationType : "interior";
    const deductibility: VatDeductibility =
      opType === "interior" ? g.vatDeductibility ?? "full" : "full";
    const round2 = (n: number) => Math.round(n * 100) / 100;

    type Emitted = {
      account: string; base: number; rate: number; cuota: number;
      subtipo: string; soportadoRoot?: string;
    };
    const emitted: Emitted[] = [];
    for (const el of expenseLines) {
      // Si es operación con auto-repercusión, forzamos IVA general (21 %) y
      // recalculamos la cuota sobre la base de la línea.
      const effectiveRate = isAutoRepercussion ? 21 : el.rate;
      const effectiveCuota = isAutoRepercussion ? Math.round(el.base * 21) / 100 : el.cuota;

      const reagp12 = opType === "interior" && isReagp12Rate(effectiveRate);
      // En compensación REAGP (12 %) A3 no tiene cuenta por defecto para el
      // subtipo 02: si no la informamos, la cuota se queda sin contrapartida y
      // el asiento entra descuadrado. Forzamos la cuenta de IVA soportado.
      const soportadoRoot = reagp12 ? "472" : undefined;

      if (deductibility === "half" && !reagp12) {
        const baseA = round2(el.base / 2);
        const cuotaA = round2(effectiveCuota / 2);
        emitted.push({
          account: el.account, base: baseA, rate: effectiveRate, cuota: cuotaA,
          subtipo: resolveSubtipo(opType, "full", effectiveRate),
        });
        emitted.push({
          account: el.account,
          base: round2(el.base - baseA),
          rate: effectiveRate,
          cuota: round2(effectiveCuota - cuotaA),
          subtipo: SUBTIPO_INTERIOR_NO_DEDUCIBLE,
        });
      } else {
        emitted.push({
          account: el.account, base: el.base, rate: effectiveRate, cuota: effectiveCuota,
          subtipo: resolveSubtipo(opType, deductibility, effectiveRate),
          soportadoRoot,
        });
      }
    }

    // Línea que soporta la retención. A3 contabiliza una única cuota de
    // retención, y debe ir en la misma fila que la base sobre la que se
    // practicó: si hay varias contrapartidas, buscamos la línea cuya base
    // coincide con irpfAbs / irpfRate. Si ninguna encaja (retención sobre el
    // total de la factura o datos incompletos), se mantiene en la última.
    const retentionIndex = (() => {
      if (irpfAbs <= 0 || emitted.length <= 1) return emitted.length - 1;
      if (irpfRate > 0) {
        const targetBase = (irpfAbs * 100) / irpfRate;
        // Tolerancia por el redondeo de la cuota al céntimo.
        const tol = Math.max(0.05, (100 / irpfRate) * 0.005 + 0.01);
        let bestIdx = -1;
        let bestDiff = Infinity;
        for (let i = 0; i < emitted.length; i++) {
          const diff = Math.abs(Math.abs(emitted[i].base) - targetBase);
          if (diff <= tol && diff < bestDiff) { bestDiff = diff; bestIdx = i; }
        }
        if (bestIdx >= 0) return bestIdx;
      }
      return emitted.length - 1;
    })();

    for (let i = 0; i < emitted.length; i++) {
      const el = emitted[i];
      const isLast = i === emitted.length - 1;
      const hasRetention = i === retentionIndex;
      lines.push(buildVatDetailRecord(
        empresa,
        entryDate,
        el.account,
        "COMPRAS",
        documentNumber,
        concept,
        isLast ? "U" : "M",
        el.base,
        el.rate,
        el.cuota,
        hasRetention ? irpfRate : 0,
        hasRetention ? irpfAbs : 0,
        opType,
        refAccs,
        el.subtipo,
        el.soportadoRoot,
        hasRetention ? resolveRetentionAccount(empresa, irpfRate) : undefined,
        g.retentionType,
      ));
    }


    // 4) Vencimiento (tipo V). Vencimiento al contado: usamos la fecha contable también
    // como fecha de la factura del vencimiento (pos 114-121), para que A3 traspase con la
    // fecha contable y no con la fecha original de la factura.
    lines.push(buildDueDateRecord(
      empresa, entryDate, g.supplierAccount, g.supplierDescription,
      documentNumber, g.invoiceNumber, totalForA3, entryDate,
      g.paymentEntry,
    ));



    // 5) Asiento de pago (cancelación) — solo si la factura está pagada al
    // contado / con tarjeta. Se emite como un asiento manual independiente
    // (dos registros tipo 0):
    //   Debe: cuenta del proveedor   por el total
    //   Haber: cuenta de tesorería    por el total
    // En facturas de abono / rectificativas el sentido se invierte (el dinero
    // vuelve a la empresa): Debe tesorería / Haber proveedor, con concepto
    // DEVOLUCIÓN FRA/...
    if (g.paymentEntry && g.paymentEntry.account) {
      const payDate = g.paymentEntry.date || entryDate;
      const payRef = documentNumber;
      const prefix = isRectificativa ? "DEVOLUCION FRA/" : "PAGO FRA/";
      const payConcept = `${prefix}${cleanText(g.invoiceNumber).toUpperCase()}`.slice(0, 30);
      const supplierSide = isRectificativa ? "H" : "D";
      const treasurySide = isRectificativa ? "D" : "H";
      lines.push(buildSimpleEntryRecord(
        empresa, payDate, g.supplierAccount, g.supplierDescription,
        supplierSide, payRef, "I", payConcept, totalForA3,
      ));
      lines.push(buildSimpleEntryRecord(
        empresa, payDate, g.paymentEntry.account,
        g.paymentEntry.kind === "card" ? "TARJETA" : "CAJA",
        treasurySide, payRef, "U", payConcept, totalForA3,
      ));
    }
  }

  // Validamos que cada línea tenga exactamente 510 chars de payload (+ CRLF al concatenar = 512 bytes)
  const normalized = lines.map((l, idx) => {
    if (l.length !== PAYLOAD_LENGTH) {
      warnings.push(`Línea ${idx + 1} con longitud ${l.length} (esperado ${PAYLOAD_LENGTH}) — corregida.`);
      return l.length > PAYLOAD_LENGTH ? l.slice(0, PAYLOAD_LENGTH) : l.padEnd(PAYLOAD_LENGTH, " ");
    }
    return l;
  });

  // Concatenamos: cada línea termina en CRLF (posiciones 511-512 según spec)
  const text = normalized.length ? normalized.join("\r\n") + "\r\n" : "";
  const bytes = encodeWin1252(text);
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const fileName = `SUENLACE_${empresa}_${today}.dat`;

  return {
    bytes,
    fileName,
    invoiceCount: groups.length,
    lineCount: lines.length,
    warnings,
  };
};

export const downloadA3File = (result: A3BuildResult): void => {
  const buffer = result.bytes.buffer.slice(result.bytes.byteOffset, result.bytes.byteOffset + result.bytes.byteLength);
  const blob = new Blob([buffer as ArrayBuffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = result.fileName;
  a.rel = "noopener";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
